import { henryBmrData } from '../shared.constants';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export const years = () => {
  const year: number[] = [];
  const maxYear = new Date().getFullYear(); //To take current year into account
  const minYear = maxYear - 99;
  for (let i = minYear; i <= maxYear; i++) {
    year.push(i);
  }
  return year;
};

export const getWeightLossPercentage = (
  currentWeight: number,
  usualWeight: number
) => {
  const weightLoss = Number(
    (((usualWeight - currentWeight) / usualWeight) * 100).toFixed()
  );
  return weightLoss > 0 ? weightLoss : 0;
};

//function to get daays list
export const getDaysInMonth = (month: number, year: number): number[] => {
  const date = new Date(year, month, 0); // 0 day of the next month gives the last day of the previous month
  return Array.from({ length: date.getDate() }, (_, i) => i + 1);
};

export const heightConversion = (feet: number, inches: number) => {
  return Number(((feet * 12 + inches) * 2.54).toFixed(1));
};

export const weightConversion = (st: number, lb: number) => {
  return Number(((st * 14 + lb) * 0.453592).toFixed(2));
};

//function to get BMI value
export const getBMI = (weight: number, height: number) => {
  const heightInMeter = Number(height / 100);
  return roundToPrecision(weight / (heightInMeter * heightInMeter));
};

//function to get BMR for Harris benedict screen
export const getHarrisBMR = (
  age: number,
  gender: string,
  weight: number,
  height: number
) => {
  if (gender === 'male') {
    return Number((66 + 13.7 * weight + 5 * height - 6.8 * age).toFixed(2));
  } else if (gender === 'female') {
    return Number((655 + 9.6 * weight + 1.7 * height - 4.7 * age).toFixed(2));
  }
  return 0;
};

//function to get BMR for mifflin screen
export const getMifflinBMR = (
  age: number,
  gender: string,
  weight: number,
  height: number
) => {
  if (gender === 'male') {
    return Number((10 * weight + 6.25 * height - 5 * age + 5).toFixed(2));
  } else if (gender === 'female') {
    return Number((10 * weight + 6.25 * height - 5 * age - 161).toFixed(2));
  }
  return 0;
};

//function to get BMR for Henry oxford screen
export const getHenryBMR = (
  age: number,
  gender: 'male' | 'female',
  weight: number
) => {
  const genderData = henryBmrData[gender];
  if (!genderData) return 0;

  let formula: { slope: number; intercept: number } | undefined;
  for (let i = 0; i < genderData.length; i++) {
    const [minAge, maxAge] = genderData[i].range.split('-');
    const min = parseInt(minAge);
    const max = maxAge === undefined ? Infinity : parseInt(maxAge); //Infinity condition for age 70+

    if (age >= min && age <= max) {
      formula = genderData[i].calculation;
      break;
    }
  }

  if (!formula) {
    return 0;
  }
  return Number((formula.slope * weight + formula.intercept).toFixed(2));
};

// function to display calculation in this format:
// Example: 10 or 10-100
// Pass [10] or [10, 100]
// Required for 10-100 kcal/day or 10 kcal/day

export const displayCalculation = (calcArr) => {
  if (!calcArr || !calcArr.length) return '0';
  if (calcArr.length === 1) return String(calcArr[0]);
  if (calcArr.length > 1) {
    return calcArr.join(' - ');
  }
};

export const displayLabel1otherOverweightAbove65Calculation = (
  calcArr,
  weight
) => {
  if (!calcArr || !calcArr.length) return '0';
  if (calcArr.length === 1) return String((calcArr[0] / weight).toFixed(2));
  if (calcArr.length > 1) {
    const newCalcArr = calcArr.map((cal) => String((cal / weight).toFixed(2)));
    return newCalcArr.join(' - ');
  }
};

// function to calculate energy(kcal/kg/day) required for provided weight(kg):
// input will be min & max energy   = result((kcal/day))
export const calEnergyWrtWeight = (input: number, weight: number) => {
  return +(input * weight).toFixed(1);
};

// function to calculate Resting Metabolic Rate(kcal) required for provided weight & height:
// input will be height, weight, minute ventilation & max body temp = result((kcal/day))
export const findRMR = (
  ve: number,
  tMax: number,
  weight: number,
  height: number
) => {
  return +(weight * 8 + height * 14 + ve * 32 + tMax * 94 - 4834).toFixed(1);
};

// function to calculate Resting Metabolic Rate(kcal) required for provided gender, age, weight & height:
// input will be gender, age, height, weight, minute ventilation & max body temp = result((kcal/day))
export const findPennRMR = (
  gender: 'male' | 'female',
  age: number,
  ve: number,
  tMax: number,
  weight: number,
  height: number
) => {
  return gender === 'male'
    ? +(
        (10 * weight + 6.25 * height - 5 * age + 5) * 0.96 +
        tMax * 167 +
        ve * 31 -
        6212
      ).toFixed(1)
    : +(
        (10 * weight + 6.25 * height - 5 * age - 161) * 0.96 +
        tMax * 167 +
        ve * 31 -
        6212
      ).toFixed(1);
};

// function to calculate Resting Metabolic Rate(kcal) required for provided gender, age, weight & height:
// input will be gender, age, height, weight, minute ventilation & max body temp = result((kcal/day))
export const findModifiedPennRMR = (
  gender: 'male' | 'female',
  age: number,
  ve: number,
  tMax: number,
  weight: number,
  height: number
) => {
  return gender === 'male'
    ? +(
        (10 * weight + 6.25 * height - 5 * age + 5) * 0.71 +
        tMax * 85 +
        ve * 64 -
        3085
      ).toFixed(1)
    : +(
        (10 * weight + 6.25 * height - 5 * age - 161) * 0.71 +
        tMax * 85 +
        ve * 64 -
        3085
      ).toFixed(1);
};

//function to find the ageInYears input between minAge and maxAge
export const isBetween = (age, min, max) => {
  return age >= min && age <= max;
};

//function to calculate the years in decimal value from years and months inputs from Patient info
export const calcYearsOfAge = (years, months) => {
  return +(years + months / 12).toFixed(1);
};

//function to calculate the months in whole number value from years and months inputs from Patient info
export const calcMonthsOfAge = (years, months) => {
  return +(years * 12 + months);
};

// Utility function to convert an SVG blob to a PNG blob.
export const convertSvgToPng = (svgBlob: Blob): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const svgText = reader.result as string;
      const image = new Image();
      // Optionally, you might set image.crossOrigin = 'anonymous'; if needed.
      image.onload = () => {
        // Use the natural dimensions of the SVG image.
        // Multiply by devicePixelRatio (or a custom scale factor) to improve quality.
        const scale = window.devicePixelRatio || 1;
        const width = image.naturalWidth;
        const height = image.naturalHeight;
        const canvas = document.createElement('canvas');
        // Set canvas dimensions to be scaled up
        canvas.width = width * scale;
        canvas.height = height * scale;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          // Scale the drawing context so the image is drawn at higher resolution
          ctx.scale(scale, scale);
          ctx.drawImage(image, 0, 0, width, height);
          canvas.toBlob(
            (pngBlob) => {
              if (pngBlob) {
                resolve(pngBlob);
              } else {
                reject(new Error('Canvas is empty'));
              }
            },
            'image/png',
            1
          );
        } else {
          reject(new Error('Failed to get canvas context'));
        }
      };
      image.onerror = (err) => {
        reject(err);
      };
      // Convert the SVG text into a data URL.
      const svgDataUrl =
        'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgText);
      image.src = svgDataUrl;
    };
    reader.onerror = (err) => {
      reject(err);
    };
    reader.readAsText(svgBlob);
  });
};

// Default handler to download the image.
// If the image is an SVG, convert it to PNG before downloading.
export const handleImageDownload = async (srcImage) => {
  if (!srcImage) return;
  try {
    const response = await fetch(srcImage, { mode: 'cors' });
    let blob = await response.blob();
    // Extract file name from URL; if missing, use a default.
    let fileName = srcImage.split('/').pop() || 'download.jpg';

    // If the file name indicates an SVG or the blob's type is image/svg+xml,
    // convert it to PNG.
    if (
      fileName.toLowerCase().endsWith('.svg') ||
      blob.type === 'image/svg+xml'
    ) {
      blob = await convertSvgToPng(blob);
      // Change the extension to .png
      fileName = fileName.replace(/\.svg$/i, '.png');
    }
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    // console.error('Error downloading image:', err);
  }
};

export const calculateMultiplication = (num, multipliedBy) => {
  const val = Number(num) * multipliedBy;
  return Number.isInteger(val) ? String(val) : val.toFixed(2);
};

export function roundIfNeeded(value: number, roundingVal = 2): string | number {
  return value % 1 === 0 ? value : parseFloat(value.toFixed(roundingVal));
}

export function roundToPrecision(value: number, precision = 2): number {
  return Number(Math.round(Number(value + 'e' + precision)) + 'e-' + precision);
}

export function calculatePediatricFluidRequirement(
  ageMonths: number,
  weightKg: number
) {
  let fluidMlPerDay = 0;

  if (ageMonths <= 6) {
    // Scenario 1: 0-6 months -> 150 mL/kg
    fluidMlPerDay = weightKg * 150;
  } else if (ageMonths >= 7 && ageMonths < 12) {
    // Scenario 1: 7-11 months -> 120 mL/kg
    fluidMlPerDay = weightKg * 120;
  } else if (ageMonths > 11) {
    if (weightKg < 10) {
      fluidMlPerDay = 100 * weightKg;
    } else {
      // Scenario 2: Children over 10kg
      if (weightKg >= 10 && weightKg <= 20) {
        fluidMlPerDay = 1000 + (weightKg - 10) * 50;
      } else {
        fluidMlPerDay = 1500 + (weightKg - 20) * 20;
      }
    }
  }

  // Calculate mL/kg/day by dividing total mL/day by weight
  const fluidMlPerKgPerDay = fluidMlPerDay / weightKg;

  return {
    fluidMlPerDay: roundToPrecision(fluidMlPerDay, 2), // Apply rounding logic
    fluidMlPerKgPerDay: roundToPrecision(fluidMlPerKgPerDay, 2) // Apply rounding logic
  };
}

export function calculateEFSAFluidRequirement(
  ageMonths: number,
  weightKg: number,
  gender?: string
): number[] {
  let fluidRequirements: number[] = [];

  if (ageMonths <= 5) {
    // 0-6 months (100-190 mL/kg/day)
    const min = weightKg * 100;
    const max = weightKg * 190;
    fluidRequirements = [min, max];
  } else if (ageMonths <= 11) {
    // 6-12 months (800-1000 mL/day)
    fluidRequirements = [800, 1000];
  } else if (ageMonths <= 23) {
    // 1-2 years (1100-1200 mL/day)
    fluidRequirements = [1100, 1200];
  } else if (ageMonths <= 36) {
    // 2-3 years (1300 mL/day)
    fluidRequirements = [1300];
  } else if (ageMonths <= 96) {
    // 4-8 years (1600 mL/day)
    fluidRequirements = [1600];
  } else if (ageMonths <= 156) {
    // 9-13 years (gender-based)
    if (gender === 'male') {
      fluidRequirements = [2100];
    } else {
      fluidRequirements = [1900];
    }
  } else {
    // 14+ years (gender-based)
    if (gender === 'male') {
      fluidRequirements = [2500];
    } else {
      fluidRequirements = [2000];
    }
  }

  // Formatting to ensure only 2 decimals if necessary
  const formatValue = (value: number) =>
    value % 1 !== 0 ? parseFloat(value.toFixed(2)) : value;

  return fluidRequirements.map(formatValue);
}

export const formatNumber = (value: number | string): string => {
  // Handle non-numeric strings (e.g., 'n/a', '1 µg/kg/d')
  if (typeof value === 'string') {
    // Check if the string is a number (e.g., "123" or "1.5")
    const numericRegex = /^-?\d+(\.\d+)?$/;
    if (numericRegex.test(value.trim())) {
      // It's a numeric string (e.g., "2.5" -> 2.5)
      value = parseFloat(value);
    } else {
      // It's a non-numeric string (e.g., 'n/a', '1 µg/kg/d') → return as-is
      return value;
    }
  }

  // Now safely handle numbers
  const roundedValue = Math.round(value * 100) / 100; // Round to 2 decimal places
  return roundedValue % 1 === 0
    ? roundedValue.toString()
    : roundedValue.toFixed(2);
};

export function calculateIOMFluidRequirement(
  ageMonths: number,
  gender?: string
): number {
  let fluidRequirements = 0;

  if (ageMonths <= 5) {
    // 0-6 months 700 mL/day
    fluidRequirements = 700;
  } else if (ageMonths <= 11) {
    // 6-12 months (800 mL/day)
    fluidRequirements = 800;
  } else if (ageMonths <= 36) {
    // 1-3 years 1300 mL/day
    fluidRequirements = 1300;
  } else if (ageMonths <= 96) {
    // 4-8 years 1700 mL/day
    fluidRequirements = 1700;
  } else if (ageMonths <= 156) {
    // 9-13 years (gender-based)
    if (gender === 'male') {
      fluidRequirements = 2400;
    } else {
      fluidRequirements = 2100;
    }
  } else {
    // 14+ years (gender-based)
    if (gender === 'male') {
      fluidRequirements = 3300;
    } else {
      fluidRequirements = 2300;
    }
  }

  return fluidRequirements;
}

//Function to find the maximum value from n number fo values
export const findMax = (...values) => Math.max(...values);

// Function to format category names
export const formatCategoryName = (title: string) => {
  const formatted = title
    .replace(/\b\w/g, (char) => char.toUpperCase()) // Capitalize first letter of each word
    .replace(/s\b/g, ''); // Remove trailing 's' to make it singular

  return formatted;
};

const pdfOptions = {
  orientation: 'portrait',
  unit: 'mm',
  format: 'a4'
};

export const addSectionToPdf = async (
  pdf,
  element,
  pdfHeading,
  includeHeading = false
) => {
  // Clone the element (without adding any heading to it)
  const clone = element.cloneNode(true);
  clone.id = element.id + '-clone';

  // Inject custom PDF styles into the clone
  const style = document.createElement('style');
  style.innerHTML = `
    body {
      margin: 0;
      padding: 0;
    }
    .pdf-section {
      padding-top: 5mm !important;
      page-break-inside: avoid;
    }
    #section-vitamins, #section-minerals {
      padding-bottom: 10mm !important;
    }
  `;
  clone.insertBefore(style, clone.firstChild);

  // Position clone off-screen for html2canvas capture
  clone.style.position = 'absolute';
  clone.style.left = '-9999px';
  document.body.appendChild(clone);

  const canvas = await html2canvas(clone, {
    scale: 1.5,
    dpi: 300,
    useCORS: true,
    letterRendering: true
  });
  document.body.removeChild(clone);

  const imgData = canvas.toDataURL('image/jpeg', 1.0);
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  const leftRightPadding = 10;
  const effectiveWidth = pageWidth - 2 * leftRightPadding;

  const imgWidth = effectiveWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  let yOffset = 0;
  if (includeHeading) {
    // Add heading text only on the first page using jsPDF text API.
    const headingText = pdfHeading;
    const headingFontSize = 25;
    pdf.setFontSize(headingFontSize);
    pdf.setFont('Calibri', 'bold');
    const textWidth = pdf.getTextWidth(headingText);
    const xPos = (pageWidth - textWidth) / 2;
    // Set the heading at a margin (for example, 20mm from the top)
    const headingMarginTop = 15;
    pdf.text(headingText, xPos, headingMarginTop);
    // Adjust yOffset so that the captured image starts below the heading (with a gap, say 10mm)
    yOffset = headingMarginTop + headingFontSize / 2 - 3;
  }

  // Add the captured image at the calculated yOffset on the first page.
  pdf.addImage(imgData, 'JPEG', leftRightPadding, yOffset, imgWidth, imgHeight);

  // Calculate how much of the image is left after the first page.
  let remainingHeight = imgHeight + yOffset - pageHeight;
  while (remainingHeight > 0) {
    pdf.addPage();
    // For subsequent pages, we shift the image upward to show the remainder.
    const position = -(pageHeight - remainingHeight);
    pdf.addImage(
      imgData,
      'JPEG',
      leftRightPadding,
      position,
      imgWidth,
      imgHeight
    );
    remainingHeight -= pageHeight;
  }
};

// Function to generate PDF as blob for sharing
const generatePdfBlob = async (pdfHeading, isSummaryPage) => {
  // Wait for DOM update
  const sectionMinerals2 = document.getElementById('section-minerals-2');
  const sectionVitamins2 = document.getElementById('section-vitamins-2');
  const elementsToBeHidden = document.querySelectorAll('.pdf-hidden-element');

  if (!isSummaryPage) {
    // Manipulate DOM for sectionMinerals2
    if (sectionMinerals2) {
      sectionMinerals2.style.display = 'block';
    }

    // Manipulate DOM from sectionVitamins2
    if (sectionVitamins2) {
      sectionVitamins2.style.display = 'block';
    }
    elementsToBeHidden.forEach((element) => (element.style.display = 'none'));
  }

  await new Promise((resolve) => setTimeout(resolve, 500));

  const pdf = new jsPDF(pdfOptions);
  const sections = [
    document.getElementById('section-macronutrients'),
    document.getElementById('section-vitamins'),
    document.getElementById('section-vitamins-2'),
    document.getElementById('section-minerals-1'),
    document.getElementById('section-minerals-2')
  ];
  const summarySections = [
    document.getElementById('section-patient-details'),
    document.getElementById('section-A'),
    document.getElementById('section-B'),
    document.getElementById('section-C')
  ];

  const sectionName = isSummaryPage ? summarySections : sections;
  for (let i = 0; i < sectionName.length; i++) {
    const section = sectionName[i];
    if (!section) continue;

    // Only include the heading for the first section.
    const includeHeading = i === 0;
    await addSectionToPdf(pdf, section, pdfHeading, includeHeading);

    if (i < sectionName.length - 1) {
      pdf.addPage();
    }
  }

  if (!isSummaryPage) {
    // Reset the Minerals Table manipulation
    if (sectionMinerals2) {
      sectionMinerals2.style.display = 'none';
    }

    if (sectionVitamins2) {
      sectionVitamins2.style.display = 'none';
    }

    elementsToBeHidden.length > 0 &&
      elementsToBeHidden.forEach(
        (element) => (element.style.display = 'table-row')
      );
  }
  return pdf.output('blob');
};
// Download PDF function
export const handleDownloadPdf = async (
  filename,
  pdfHeading,
  isSummaryPage = false
) => {
  try {
    const blob = await generatePdfBlob(pdfHeading, isSummaryPage);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (error) {
    // console.error('Error generating PDF:', error);
  }
};

// Share PDF via email function
export const handleSharePdf = async (
  filename,
  pdfName,
  pdfHeading,
  isSummaryPage = false
) => {
  if (navigator.share) {
    try {
      const blob = await generatePdfBlob(pdfHeading, isSummaryPage);
      const file = new File([blob], filename, {
        type: 'application/pdf'
      });

      await navigator.share({
        files: [file],
        title: pdfName,
        text: isSummaryPage
          ? 'Here is the summary'
          : 'Here is the nutrition comparison report'
      });

      // resolve(true)
    } catch (error) {
      // console.error('Error sharing PDF:', error);
      // Fallback to download if sharing fails
      handleDownloadPdf(filename, pdfHeading);
    }
  } else {
    // Fallback for browsers without Web Share API
    const emailSubject = isSummaryPage
      ? 'Summary of the Patient'
      : 'Nutrition Comparison Report';
    const emailBody = isSummaryPage
      ? 'Please find attached summary details'
      : 'Please find attached the nutrition comparison report.';

    // Generate PDF and create object URL
    const blob = await generatePdfBlob(pdfHeading, isSummaryPage);
    const pdfUrl = URL.createObjectURL(blob);

    // Create a temporary link for download
    const a = document.createElement('a');
    a.href = `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
    a.click();

    // Also show download option since mailto: can't attach files directly
    setTimeout(() => {
      if (
        window.confirm(
          "Your browser doesn't support direct sharing. Would you like to download the PDF instead?"
        )
      ) {
        a.href = pdfUrl;
        a.download = filename;
        a.click();
      }
      URL.revokeObjectURL(pdfUrl);
    }, 100);
  }
};

// to calculate the summed value of RNIs in RNI tableview page
export const calculateRNISummedValue = (
  val1: any,
  val2: any
): string | 'N/A' => {
  // only 1 product is there and the other one is N/A
  if (val1 === 'N/A' && !val2) {
    return 'N/A';
  }

  // only 1 product is there and that one is not N/A
  if (val1 !== 'N/A' && !val2) {
    return Number(val1).toFixed(2);
  }
  // Both are 'N/A'
  if (val1 === 'N/A' && val2 === 'N/A') {
    return 'N/A';
  }

  // Convert 'N/A' to 0, otherwise convert to number
  const num1 = val1 === 'N/A' ? 0 : Number(val1);
  const num2 = val2 === 'N/A' ? 0 : Number(val2);

  return Number(num1 + num2).toFixed(2);
};
